import React from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { formatCurrency } from "utils";
import { RATE_FREQUENCIES } from "constants/index";
import DisplayBox from "components/DisplayBox";
import { PreferredRateTag } from "components/Profile";
import { TAG_SIZE } from "components/Tags";
import Tooltip from "components/Tooltip";

/**
 * ApplicationRate
 *
 * @param {Object} data
 */
const ApplicationRate = ({ data, ...props }) => {
  const { hourlyRateData, rate } = data || {};
  const {
    applicantHourlyRateUsd,
    profileHourlyRateUsd,
    missionRoleTargetRateUsd,
    minimumApplicantHourlyRateUsd,
    minimumAssignedHourlyRateUsd,
    consideredHourlyRateUsd,
    currency,
  } = hourlyRateData || {};
  const { amount: actualRate, currency: actualCurrency, frequency: actualFrequency } = rate || {};
  const frequency = RATE_FREQUENCIES.hourly;
  const isApplicantRateProcessing = actualRate && !applicantHourlyRateUsd;

  return (
    <DisplayBox
      numColumns={3}
      hideHeader
      data={[
        {
          title: <FormattedMessage id="MissionRoleApplicant.DisplayBox.TitleRate" />,
          body: (
            <Tooltip
              tooltipId="Global.RateWithFrequency"
              values={{
                rate: actualRate ? formatCurrency(actualRate, actualCurrency) : <FormattedMessage id="Global.EmDash" />,
                frequency: actualFrequency ? (
                  <FormattedMessage
                    id={`Global.${actualFrequency === RATE_FREQUENCIES.hourly ? `PerHour` : `PerMonth`}`}
                  />
                ) : (
                  <FormattedMessage id="Global.EmDash" />
                ),
              }}
            >
              <PreferredRateTag
                rate={{ amount: applicantHourlyRateUsd, currency, frequency }}
                size={TAG_SIZE.medium}
                emptyMsgId={
                  isApplicantRateProcessing ? "MissionRoleApplicant.DisplayBox.ValueInProcess" : "Global.NotProvided"
                }
              />
            </Tooltip>
          ),
        },
        {
          title: <FormattedMessage id="MissionRoleApplicant.DisplayBox.TitlePreferredRate" />,
          body: (
            <PreferredRateTag rate={{ amount: profileHourlyRateUsd, currency, frequency }} size={TAG_SIZE.medium} />
          ),
        },
        {
          title: (
            <Tooltip
              tooltipId="MissionRoleApplicant.DisplayBox.Tooltip.CurrentSuggestedRate"
              values={{
                rate: formatCurrency(data?.recommendedRate?.amount, data?.recommendedRate?.currency),
                frequency: (
                  <FormattedMessage
                    id={`Global.${
                      data?.recommendedRate?.frequency === RATE_FREQUENCIES.hourly ? `PerHour` : `PerMonth`
                    }`}
                  />
                ),
              }}
            >
              <FormattedMessage id="MissionRoleApplicant.DisplayBox.TitleSuggestedRate" />
            </Tooltip>
          ),
          body: <PreferredRateTag rate={data?.lockedRecommendedRate || data?.recommendedRate} size={TAG_SIZE.medium} />,
        },
        {
          title: <FormattedMessage id="MissionRoleApplicant.DisplayBox.TitleTargetRate" />,
          body: (
            <PreferredRateTag rate={{ amount: missionRoleTargetRateUsd, currency, frequency }} size={TAG_SIZE.medium} />
          ),
        },
        {
          title: <FormattedMessage id="MissionRoleApplicant.DisplayBox.TitleMinimumApplicantRate" />,
          body: (
            <PreferredRateTag
              rate={{ amount: minimumApplicantHourlyRateUsd, currency, frequency }}
              size={TAG_SIZE.medium}
            />
          ),
        },
        {
          title: <FormattedMessage id="MissionRoleApplicant.DisplayBox.TitleMinimumAssignedRate" />,
          body: (
            <PreferredRateTag
              rate={{ amount: minimumAssignedHourlyRateUsd, currency, frequency }}
              size={TAG_SIZE.medium}
            />
          ),
        },
        {
          title: <FormattedMessage id="MissionRoleApplicant.DisplayBox.TitleConsideredRate" />,
          body: (
            <PreferredRateTag rate={{ amount: consideredHourlyRateUsd, currency, frequency }} size={TAG_SIZE.medium} />
          ),
        },
      ]}
      {...props}
    />
  );
};

ApplicationRate.propTypes = {
  data: PropTypes.object.isRequired,
};

export default ApplicationRate;
